import type { NotificationConfig } from ".";
import { type BrandingSnapshot, resolveEmailBrand } from "./email-brand";
import { type EmailLayout, renderEmailDocument } from "./email-layout";
import { htmlToPlainText } from "./email-plain-text";
import {
	findSubmitterEmail,
	resolveEmailDelivery,
} from "./notification-delivery";
import {
	renderFormTemplateHtml,
	renderFormTemplateSubject,
} from "./render-form-template";
import { allowConfirmationEmail } from "./submit-rate-limit";

export interface DispatchEmail {
	bcc?: string[];
	cc?: string[];
	html: string;
	plainText: string;
	replyTo?: string;
	subject: string;
	to: string[];
}

export interface DispatchContext {
	data: Record<string, unknown>;
	formId: string;
	formName: string;
	formSlug: string;
	/** Site Branding + the region's email layout around every message. */
	look: { branding: BrandingSnapshot; layout: EmailLayout };
	pageTitle?: string;
	pageUrl?: string;
	/** `form.schema.root` — finds the client's Email field and labels `{{all_fields}}`. */
	schemaRoot: unknown;
	submissionId: string;
}

export interface DispatchDeps {
	/** The app's own sender (it loads that region's email connection); must never throw. */
	sendEmail: (
		email: DispatchEmail
	) => Promise<{ ok: true } | { ok: false; error: string }>;
}

/** What happened to this lead's emails: stored on the submission so failures are visible. */
export interface DispatchOutcome {
	email: "sent" | "failed" | "none";
	error?: string;
}

const WEBHOOK_TIMEOUT_MS = 10_000;

async function hmacHex(secret: string, payload: string): Promise<string> {
	const enc = new TextEncoder();
	const key = await crypto.subtle.importKey(
		"raw",
		enc.encode(secret),
		{ name: "HMAC", hash: "SHA-256" },
		false,
		["sign"]
	);
	const sig = await crypto.subtle.sign("HMAC", key, enc.encode(payload));
	return Array.from(new Uint8Array(sig), (b) =>
		b.toString(16).padStart(2, "0")
	).join("");
}

async function sendWebhook(
	n: Extract<NotificationConfig, { type: "webhook" }>,
	ctx: DispatchContext
): Promise<void> {
	const payload = JSON.stringify({
		formId: ctx.formId,
		formSlug: ctx.formSlug,
		submissionId: ctx.submissionId,
		data: ctx.data,
	});
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		...(n.headers ?? {}),
	};
	if (n.secret) {
		headers["X-Signature"] = await hmacHex(n.secret, payload);
	}
	const res = await fetch(n.url, {
		method: "POST",
		headers,
		body: payload,
		signal: AbortSignal.timeout(WEBHOOK_TIMEOUT_MS),
	});
	if (!res.ok) {
		console.error("[forms] webhook failed", {
			status: res.status,
			submissionId: ctx.submissionId,
		});
	}
}

/** One email entry: `null` when it doesn't send on purpose (off, no client address, throttled). */
async function sendOne(
	n: Extract<NotificationConfig, { type: "email" }>,
	ctx: DispatchContext,
	deps: DispatchDeps
): Promise<{ ok: true } | { ok: false; error: string } | null> {
	// An admin email that's on but has nobody to go to is a failure, not "nothing to send".
	if (
		(n.audience ?? "admin") === "admin" &&
		n.mode !== "off" &&
		!n.to?.length
	) {
		return { ok: false, error: "no recipient set" };
	}
	const delivery = resolveEmailDelivery(
		n,
		findSubmitterEmail(ctx.schemaRoot, ctx.data, n.toField)
	);
	if (!delivery) {
		return null;
	}
	const client = delivery.audience === "submitter" ? delivery.to[0] : undefined;
	if (client && !allowConfirmationEmail(client)) {
		console.warn("[forms] client confirmation throttled", {
			submissionId: ctx.submissionId,
		});
		return null;
	}
	const options = { audience: delivery.audience };
	const html = renderEmailDocument(
		renderFormTemplateHtml(n.bodyTemplate ?? "", ctx.data, ctx, options),
		ctx.look.layout,
		resolveEmailBrand(ctx.look.layout, ctx.look.branding),
		n
	);
	return await deps.sendEmail({
		to: delivery.to,
		...(delivery.cc ? { cc: delivery.cc } : {}),
		...(delivery.bcc ? { bcc: delivery.bcc } : {}),
		subject: renderFormTemplateSubject(n.subject ?? "", ctx.data, ctx, options),
		html,
		plainText: htmlToPlainText(html),
		...(delivery.replyTo ? { replyTo: delivery.replyTo } : {}),
	});
}

/**
 * Sends a lead's emails and webhooks — the one copy both submit paths use. Never throws;
 * webhook failures are logged only, email results come back for the lead row.
 */
export async function dispatchFormNotifications(
	notifications: NotificationConfig[] | null | undefined,
	ctx: DispatchContext,
	deps: DispatchDeps
): Promise<DispatchOutcome> {
	const errors: string[] = [];
	let sent = 0;
	await Promise.allSettled(
		(notifications ?? []).map(async (n) => {
			try {
				if (n.type === "webhook") {
					await sendWebhook(n, ctx);
					return;
				}
				const result = await sendOne(n, ctx, deps);
				if (!result) {
					return;
				}
				// `in` narrows even where strictNullChecks is off (the builder package).
				if (!("error" in result)) {
					sent += 1;
					return;
				}
				errors.push(`${n.audience ?? "admin"}: ${result.error}`);
				console.error("[forms] email notification failed", {
					audience: n.audience ?? "admin",
					error: result.error,
					formSlug: ctx.formSlug,
					submissionId: ctx.submissionId,
				});
			} catch (err) {
				if (n.type === "email") {
					errors.push(`${n.audience ?? "admin"}: ${String(err)}`);
				}
				console.error("[forms] notification dispatch failed", err);
			}
		})
	);
	if (errors.length > 0) {
		return { email: "failed", error: errors.join("; ").slice(0, 1000) };
	}
	return { email: sent > 0 ? "sent" : "none" };
}
